import dayjs, { type Dayjs } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import momentTimezone from 'moment-timezone';
import { t } from 'ttag';
import { US_DAYLIGHT_SAVING_TIME_UTC, US_STANDARD_TIME_UTC } from './usTimezones';

momentDurationFormatSetup(moment);

dayjs.extend(duration);
dayjs.extend(utc);

/**
 * Trims padding zeros out of a timezone label so offsets read as 'GMT+5' rather than 'GMT+05'. Drops a '+00' segment when present, otherwise strips every '0' character, and returns an empty string for a missing label.
 *
 * @param timezone - Timezone label to condense.
 * @returns Condensed timezone label, or an empty string.
 */
const removeZeroTimezone = (timezone?: string) => {
  if (!timezone) {
    return '';
  }
  if (timezone.includes('+00')) {
    return timezone.replaceAll('+00', '');
  }
  if (timezone.includes('0')) {
    return timezone.replaceAll('0', '');
  }
  return timezone;
};

/**
 * Shifts a study timestamp by the study's hour offset, or back to server time when reverting.
 * Clones the date, negates the offset when shouldRevertToServerTime is set, and adds or subtracts
 * that many hours, leaving the clone unchanged for a zero offset.
 *
 * @param props - Options carrying dateMoment, studyTimeOffset and shouldRevertToServerTime.
 * @returns The shifted copy of the date.
 */
const adjustStudyTime = ({
  dateMoment,
  studyTimeOffset = 0,
  shouldRevertToServerTime = false,
}: {
  dateMoment: Dayjs | moment.Moment;
  studyTimeOffset?: number;
  shouldRevertToServerTime?: boolean;
}) => {
  let newDateMoment: any = dateMoment.clone();
  let newStudyTimeOffset = studyTimeOffset;
  if (shouldRevertToServerTime) {
    newStudyTimeOffset *= -1;
  }
  if (newStudyTimeOffset) {
    if (newStudyTimeOffset > 0) {
      newDateMoment = newDateMoment.add(newStudyTimeOffset, 'hours');
    } else {
      newDateMoment = newDateMoment.subtract(Math.abs(newStudyTimeOffset), 'hours');
    }
  }
  return newDateMoment;
};

/**
 * Builds the timezone label shown next to a study date, correcting it for US daylight saving.
 * Returns the condensed raw label for non-US or missing facilities, otherwise shifts the offset by an
 * hour when DST differs between study start and the date, then maps it to a named US zone.
 *
 * @param props - Options carrying startStudyDate, isoDate, timezone, studyTimeOffset and facility.
 * @returns The condensed timezone label.
 */
const formatTimezone = ({
  startStudyDate,
  isoDate,
  timezone,
  studyTimeOffset,
  facility,
}: {
  startStudyDate?: string;
  isoDate: Dayjs | string;
  timezone?: string;
  studyTimeOffset?: number;
  facility?: {
    contact?: {
      country?: string;
    };
    timezone?: string;
  };
}) => {
  if (!facility || facility.contact?.country !== 'US' || !isoDate) {
    return removeZeroTimezone(timezone);
  }

  if (startStudyDate) {
    const isStartStudyDateDST = adjustStudyTime({
      dateMoment: momentTimezone.tz(startStudyDate, facility.timezone as string),
      studyTimeOffset,
    }).isDST();
    const isIsoDateDST = momentTimezone.tz(isoDate, facility.timezone as string).isDST();
    let newTimezone: string | number = _.toNumber(_.split(timezone, 'UTC')[1]);
    if (!isStartStudyDateDST && isIsoDateDST) {
      newTimezone += 1;
    }
    if (isStartStudyDateDST && !isIsoDateDST) {
      newTimezone -= 1;
    }
    const newTimezoneString = `UTC${newTimezone > 0 ? '+' : ''}${newTimezone}`;
    /**
     * Finds the UTC timezone descriptor whose utc field matches newTimezoneString, selecting the daylight-saving list when isIsoDateDST is set and the standard list otherwise. Derived via a lodash find over the two US timezone offset tables.
     *
     * @returns The matching timezone entry, or undefined when none matches.
     */
    const utc = _.find(
      isIsoDateDST ? US_DAYLIGHT_SAVING_TIME_UTC : US_STANDARD_TIME_UTC,
      (x) => x.utc === newTimezoneString,
    );
    if (utc) {
      return removeZeroTimezone(utc.name);
    }
  }

  const isDST = momentTimezone.tz(isoDate, facility.timezone as string).isDST();
  /**
   * Finds the UTC timezone descriptor whose utc field matches the timezone value, selecting the daylight-saving list when isDST is set and the standard list otherwise. Derived via a lodash find over the two US timezone offset tables.
   *
   * @returns The matching timezone entry, or undefined when none matches.
   */
  const utc = _.find(
    isDST ? US_DAYLIGHT_SAVING_TIME_UTC : US_STANDARD_TIME_UTC,
    (x) => x.utc === timezone,
  );
  if (utc) {
    return removeZeroTimezone(utc.name);
  }
  return removeZeroTimezone(timezone);
};

/**
 * Converts a 'GMT+hh' style timezone label into an offset in minutes. Parses the numeric part after the first three characters and multiplies it by 60, falling back to zero if parsing throws.
 *
 * @param timezone - Timezone label whose hour offset is parsed.
 * @returns Offset in minutes, or zero when the label could not be parsed.
 */
const timezoneToOffset = (timezone?: string | null) => {
  if (!timezone) {
    return 0;
  }
  try {
    const timeString = timezone.substring(3);
    return parseInt(timeString, 10) * 60;
  } catch {
    return 0;
  }
};

/**
 * Computes the UTC offset to display a study date at, accounting for US daylight saving changes.
 * Parses the study timezone into minutes and, for US facilities with a study start, adds or removes
 * an hour when the date and the study start fall on different sides of a DST switch.
 *
 * @param props - Options carrying startStudyDate, isoDate, timezone, studyTimeOffset,
 * facilityCountry and facilityTimezone.
 * @returns An object carrying the utcOffset in minutes and the adjusted newTimezone label.
 */
const getUtcOffsetWithDst = ({
  startStudyDate,
  isoDate,
  timezone,
  studyTimeOffset,
  facilityCountry,
  facilityTimezone = 'America/New_York',
}: {
  startStudyDate?: string;
  isoDate: Dayjs | string;
  timezone?: string;
  studyTimeOffset?: number;
  facilityCountry?: string;
  facilityTimezone?: string;
}) => {
  let utcOffset = timezoneToOffset(timezone);
  let newTimezone = _.toNumber(_.split(timezone, 'UTC')[1]);
  if (startStudyDate && facilityCountry === 'US') {
    const isStartStudyDateDST = adjustStudyTime({
      dateMoment: momentTimezone.tz(startStudyDate, facilityTimezone),
      studyTimeOffset,
    }).isDST();
    // moment cannot parse a Dayjs object and falls back to today, which would check DST on the wrong day
    const isoDateInput = dayjs.isDayjs(isoDate) ? isoDate.toISOString() : isoDate;
    const isIsoDateDST = adjustStudyTime({
      dateMoment: momentTimezone.tz(isoDateInput, facilityTimezone),
      studyTimeOffset,
    }).isDST();
    if (!isStartStudyDateDST && isIsoDateDST) {
      // *: From standard to DST
      utcOffset += 60;
      newTimezone += 1;
    }
    if (isStartStudyDateDST && !isIsoDateDST) {
      // *: From DST to standard
      utcOffset -= 60;
      newTimezone -= 1;
    }
    // *: 2 dates are in the same time zone range or facility is in time zone that do not observe DST, do nothing
  }
  return {
    utcOffset,
    newTimezone: `UTC${newTimezone > 0 ? '+' : ''}${newTimezone}`,
  };
};

/**
 * Formats a study date in the study's DST-aware timezone using the facility's country and zone.
 * Resolves the offset through getUtcOffsetWithDst, applies it and the study hour offset, and formats
 * with the given pattern or MM/DD/YYYY, HH:mm:ss, returning an empty string for a missing or invalid
 * date.
 *
 * @param props - Options carrying startStudyDate, isoDate, timezone, studyTimeOffset, facility and
 * format.
 * @returns The formatted date, or an empty string.
 */
const dateToStringWithDailyTimeZone = ({
  startStudyDate,
  isoDate,
  timezone = 'UTC+0',
  studyTimeOffset = 0,
  facility,
  format,
}: {
  startStudyDate?: string;
  isoDate?: Dayjs | string;
  timezone?: string;
  studyTimeOffset?: number;
  facility?: {
    contact?: {
      country?: string;
    };
    timezone?: string;
  };
  format?: string;
}) => {
  if (isoDate) {
    const { utcOffset } = getUtcOffsetWithDst({
      startStudyDate,
      isoDate,
      timezone,
      studyTimeOffset,
      facilityCountry: facility?.contact?.country,
      facilityTimezone: facility?.timezone,
    });
    let dateMoment = dayjs(isoDate).utcOffset(utcOffset, false);
    dateMoment = adjustStudyTime({
      dateMoment,
      studyTimeOffset,
    });
    return dateMoment.isValid()
      ? format
        ? dateMoment.format(format)
        : dateMoment.format('MM/DD/YYYY, HH:mm:ss')
      : '';
  }
  return '';
};

/**
 * Formats a study date in the study's DST-aware timezone for display.
 * Resolves the offset through getUtcOffsetWithDst, applies it and the study hour offset, and formats
 * with the given pattern, returning an empty string for a missing or invalid date.
 *
 * @param props - Options carrying startStudyDate, isoDate, timezone, studyTimeOffset,
 * facilityCountry, facilityTimezone and format.
 * @returns The formatted date, or an empty string.
 */
const dateToString = ({
  startStudyDate,
  isoDate,
  timezone = 'UTC+0',
  studyTimeOffset = 0,
  facilityCountry,
  facilityTimezone,
  format = 'MM/DD/YYYY, HH:mm:ss',
}: {
  startStudyDate?: string;
  isoDate: Dayjs | string | undefined;
  timezone?: string;
  studyTimeOffset?: number;
  facilityCountry?: string;
  facilityTimezone?: string;
  format?: string;
}) => {
  if (isoDate) {
    const { utcOffset } = getUtcOffsetWithDst({
      startStudyDate,
      isoDate,
      timezone,
      studyTimeOffset,
      facilityCountry,
      facilityTimezone,
    });
    let dateMoment = dayjs(isoDate).utcOffset(utcOffset);
    dateMoment = adjustStudyTime({
      dateMoment,
      studyTimeOffset,
    });
    return dateMoment.isValid() ? dateMoment.format(format) : '';
  }
  return '';
};

/**
 * Turns a picked report date into the start-of-day or end-of-day timestamp in the study's timezone.
 * Parses the date, optionally with a format, keeps its local time while applying the DST-aware offset
 * and the study hour offset, then takes the start or end of that day.
 *
 * @param props - Options carrying startStudyDate, date, timezone, studyTimeOffset, facilityCountry,
 * facilityTimezone, type and format.
 * @returns The day boundary as epoch milliseconds.
 */
const dateToReportDate = ({
  startStudyDate,
  date,
  timezone,
  studyTimeOffset = 0,
  facilityCountry,
  facilityTimezone,
  type,
  format,
}: {
  startStudyDate?: string;
  date: Dayjs | string;
  timezone?: string;
  studyTimeOffset?: number;
  facilityCountry?: string;
  facilityTimezone?: string;
  type: 'start' | 'end';
  format?: string;
}) => {
  const { utcOffset } = getUtcOffsetWithDst({
    startStudyDate,
    isoDate: date,
    timezone,
    studyTimeOffset,
    facilityCountry,
    facilityTimezone,
  });
  let momentDate = format
    ? dayjs(date, format).utcOffset(utcOffset, true)
    : dayjs(date).utcOffset(utcOffset, true);
  momentDate = adjustStudyTime({
    dateMoment: momentDate,
    studyTimeOffset,
  });
  if (type === 'start') {
    const result = momentDate.clone().startOf('day').valueOf();
    return result;
  }
  const result = momentDate.clone().endOf('day').valueOf();
  return result;
};

/**
 * Formats a study date for use in a report file name, in the study's DST-aware timezone.
 * Resolves the offset through getUtcOffsetWithDst, applies it and the study hour offset, and formats
 * as YYYYMMDD_HHmm, returning an empty string for a missing or invalid date.
 *
 * @param props - Options carrying startStudyDate, isoDate, studyTimeOffset, timezone,
 * facilityCountry and facilityTimezone.
 * @returns The file-name date string, or an empty string.
 */
const dataToStringForNameReport = ({
  startStudyDate,
  isoDate,
  studyTimeOffset = 0,
  timezone = 'UTC+0',
  facilityCountry,
  facilityTimezone,
}: {
  startStudyDate?: string;
  isoDate: Dayjs | string;
  studyTimeOffset?: number;
  timezone?: string;
  facilityCountry?: string;
  facilityTimezone?: string;
}) => {
  if (isoDate) {
    const { utcOffset } = getUtcOffsetWithDst({
      startStudyDate,
      isoDate,
      timezone,
      studyTimeOffset,
      facilityCountry,
      facilityTimezone,
    });
    let dateMoment = dayjs(isoDate).utcOffset(utcOffset, false);
    dateMoment = adjustStudyTime({
      dateMoment,
      studyTimeOffset,
    });
    return dateMoment.isValid() ? dateMoment.format('YYYYMMDD_HHmm') : '';
  }

  return '';
};

/**
 * Measures how long ago a timestamp was, for 'last connect' style readouts. Normalises now to its own utc offset and diffs it against the parsed time, in the requested unit when one is given and in milliseconds otherwise, returning null for a missing time.
 *
 * @param time - Timestamp to measure from.
 * @param measurements - Unit to express the difference in; milliseconds when omitted.
 * @returns Elapsed time in the requested unit, or null when no timestamp was given.
 */
const dateToDuration = (
  time: string | undefined,
  measurements?: dayjs.OpUnitType,
): number | null => {
  if (time) {
    const dateMoment = dayjs(time);
    let now = dayjs();
    now = now.utcOffset(now.utcOffset(), false);
    // *: Get the difference between the moments
    const diff = measurements ? now.diff(dateMoment, measurements) : now.diff(dateMoment);
    return diff;
  }

  return null;
};

/**
 * Renders an elapsed millisecond difference as a translated 'x ago' phrase. Walks the duration from years down to minutes and returns the first non-zero unit with singular or plural wording, falling back to 'Just now' for anything under a minute and to an empty string for a falsy difference.
 *
 * @param diff - Elapsed time in milliseconds.
 * @returns Localised 'ago' phrase, 'Just now', or an empty string.
 */
const dateToTimeSince = (diff: number | null) => {
  // *: Express as a duration
  if (diff) {
    const diffDuration = dayjs.duration(diff);
    if (diffDuration.years() > 0) {
      return `${diffDuration.years()} ${diffDuration.years() === 1 ? t`year ago` : t`years ago`}`;
    }
    if (diffDuration.months() > 0) {
      return `${diffDuration.months()} ${diffDuration.months() === 1 ? t`month ago` : t`months ago`}`;
    }
    if (diffDuration.days() > 0) {
      return `${diffDuration.days()} ${diffDuration.days() === 1 ? t`day ago` : t`days ago`}`;
    }
    if (diffDuration.hours() > 0) {
      return `${diffDuration.hours()} ${diffDuration.hours() === 1 ? t`hour ago` : t`hours ago`}`;
    }
    if (diffDuration.minutes() > 0) {
      return `${diffDuration.minutes()} ${diffDuration.minutes() === 1 ? t`minute ago` : t`minutes ago`}`;
    }
    return t`Just now`;
  }
  return '';
};

/**
 * Moves an existing date object into the study's DST-aware timezone.
 * Clones the date, applies the offset from getUtcOffsetWithDst and the study hour offset, and returns
 * null for a missing or invalid result.
 *
 * @param props - Options carrying startStudyDate, dateMoment, timezone, studyTimeOffset,
 * facilityCountry and facilityTimezone.
 * @returns The date in study time, or null.
 */
const addTimezoneToMoment = ({
  startStudyDate,
  dateMoment,
  timezone = 'UTC+0',
  studyTimeOffset = 0,
  facilityCountry,
  facilityTimezone,
}: {
  startStudyDate?: string;
  dateMoment: Dayjs | null;
  timezone?: string;
  studyTimeOffset?: number;
  facilityCountry?: string;
  facilityTimezone?: string;
}) => {
  if (dateMoment) {
    let clonedDateMoment = dateMoment.clone();
    const { utcOffset } = getUtcOffsetWithDst({
      startStudyDate,
      isoDate: clonedDateMoment,
      timezone,
      studyTimeOffset,
      facilityCountry,
      facilityTimezone,
    });
    clonedDateMoment = clonedDateMoment.utcOffset(utcOffset, false);
    clonedDateMoment = adjustStudyTime({
      dateMoment: clonedDateMoment,
      studyTimeOffset,
    });
    return clonedDateMoment.isValid() ? clonedDateMoment : null;
  }
  return null;
};

/**
 * Parses a study date string into a date object in the study's DST-aware timezone.
 * Applies the offset from getUtcOffsetWithDst and the study hour offset to the parsed date, and
 * returns null for a missing or invalid date.
 *
 * @param props - Options carrying startStudyDate, isoDate, timezone, studyTimeOffset,
 * facilityCountry and facilityTimezone.
 * @returns The date in study time, or null.
 */
const convertToMoment = ({
  startStudyDate,
  isoDate,
  timezone = 'UTC+0',
  studyTimeOffset = 0,
  facilityCountry,
  facilityTimezone,
}: {
  startStudyDate?: string;
  isoDate: Dayjs | string;
  timezone?: string;
  studyTimeOffset?: number;
  facilityCountry?: string;
  facilityTimezone?: string;
}) => {
  if (isoDate) {
    const { utcOffset } = getUtcOffsetWithDst({
      startStudyDate,
      isoDate,
      timezone,
      studyTimeOffset,
      facilityCountry,
      facilityTimezone,
    });
    let dateMoment = dayjs(isoDate).utcOffset(utcOffset, false);
    dateMoment = adjustStudyTime({
      dateMoment,
      studyTimeOffset,
    });
    return dateMoment.isValid() ? dateMoment : null;
  }
  return null;
};

/**
 * Computes a study's length in whole minutes from its start and stop timestamps. Subtracts the parsed times, converts the millisecond difference to minutes and rounds it, returning zero when either bound is missing.
 *
 * @param start - Study start timestamp.
 * @param stop - Study stop timestamp.
 * @returns Study duration in rounded minutes, or zero.
 */
const getStudyDuration = (start: string, stop: string): number => {
  if (start && stop) {
    const duration = new Date(stop).getTime() - new Date(start).getTime();
    return Math.round(duration / 1000 / 60);
  }
  return 0;
};

/**
 * Renders a study's duration as a readable string such as 2d 03h 15m or 7 day.
 * Uses the given duration or derives minutes from start and stop, formats it, patches a 30-day
 * rollover, trims leading zero days or hours per stopTrim, and pluralises day past 1440 minutes.
 *
 * @param props - Options carrying start, stop, duration, format, unit and stopTrim.
 * @returns The trimmed duration string.
 */
const getStudyDurationString = ({
  start,
  stop,
  duration,
  format = 'D[d] HH[h] mm[m]',
  unit = 'minutes',
  stopTrim,
}: {
  start: string;
  stop: string;
  duration?: number;
  format?: string;
  unit?: 'minutes' | 'hours' | 'days';
  stopTrim?: 'H' | 'm' | '';
}) => {
  let calculateDuration;
  if (!_.isNil(duration)) {
    calculateDuration = duration;
  } else {
    calculateDuration = getStudyDuration(start, stop);
  }

  let durationString;

  const dayjsDuration = dayjs.duration(calculateDuration, unit);
  if (format === 'D [day]') {
    const days = dayjsDuration.asDays();
    durationString = `${days} day`;
  } else if (format === 'D[d] HH[h] mm[m]') {
    const days = dayjsDuration.asDays();
    durationString = dayjsDuration.format(format);
    if (days === 30 && _.includes(durationString, '0d')) {
      durationString = _.replace(durationString, '0d', '30d');
    }
  } else {
    durationString = dayjsDuration.format(format);
  }

  switch (stopTrim) {
    case 'H': {
      if (!_.includes(durationString, '30d')) {
        durationString = _.replace(durationString, '0d', '');
      }
      break;
    }
    case 'm': {
      durationString = _.replace(durationString, '0h', '');
      break;
    }
    default: {
      break;
    }
  }

  if (unit === 'minutes' && calculateDuration > 1440 && _.includes(format, 'day')) {
    durationString = _.replace(durationString, 'day', 'days');
  }

  durationString = _.trim(durationString);

  return durationString;
};

/**
 * Computes how many calendar days the study hour offset shifts the study's start date.
 * Converts the start date to study time with and without studyTimeOffset and diffs their start of
 * day in days, returning zero when there is no start date or either conversion fails.
 *
 * @param props - Options carrying startStudyDate, timezone and studyTimeOffset.
 * @returns The day offset, or zero.
 */
const getAdjustedStudyDayOffset = ({
  startStudyDate,
  timezone,
  studyTimeOffset,
}: {
  startStudyDate?: string;
  timezone?: string;
  studyTimeOffset?: number;
}) => {
  if (startStudyDate) {
    const startStudyMoment = convertToMoment({
      isoDate: startStudyDate,
      timezone,
    });
    const adjustedStartStudyMoment = convertToMoment({
      isoDate: startStudyDate,
      timezone,
      studyTimeOffset,
    });
    if (!startStudyMoment || !adjustedStartStudyMoment) {
      return 0;
    }
    const diffDays = adjustedStartStudyMoment
      .startOf('days')
      .diff(startStudyMoment.startOf('days'), 'day');
    return diffDays;
  }
  return 0;
};

/**
 * Shifts a date by the study's day offset, or back to the server date when reverting.
 * Returns the date unchanged for a zero offset, otherwise adds the offset in days to a clone, negated
 * when shouldRevertToServerDate is set.
 *
 * @param props - Options carrying dateMoment, adjustedStudyDayOffset and shouldRevertToServerDate.
 * @returns The shifted date, or the original one for a zero offset.
 */
const adjustStudyDate = ({
  dateMoment,
  adjustedStudyDayOffset,
  shouldRevertToServerDate = false,
}: {
  dateMoment: Dayjs;
  adjustedStudyDayOffset: number;
  shouldRevertToServerDate?: boolean;
}) =>
  adjustedStudyDayOffset
    ? shouldRevertToServerDate
      ? dateMoment.clone().add(adjustedStudyDayOffset * -1, 'day')
      : dateMoment.clone().add(adjustedStudyDayOffset, 'day')
    : dateMoment;

/**
 * Renders a study's configured duration in whole days when it lines up with day boundaries and in hours otherwise. Builds a moment duration from the minute total and formats it as days when the total is an exact multiple of 1440 minutes, as hours otherwise.
 *
 * @param totalTime - Duration in minutes.
 * @returns Duration formatted in days or hours.
 */
const formatInitialDuration = (totalTime: number) => {
  const duration = moment.duration(totalTime, 'minutes');
  if (duration.asMinutes() % 1440 === 0) {
    return duration.format('d [days]');
  }
  return duration.format('h [hours]');
};

export {
  addTimezoneToMoment,
  adjustStudyDate,
  adjustStudyTime,
  convertToMoment,
  dataToStringForNameReport,
  dateToDuration,
  dateToReportDate,
  dateToString,
  dateToStringWithDailyTimeZone,
  dateToTimeSince,
  formatInitialDuration,
  formatTimezone,
  getAdjustedStudyDayOffset,
  getStudyDuration,
  getStudyDurationString,
  timezoneToOffset,
};
